import {ReactNode} from 'react';

import {RowData, RowProperties} from 'notebook/tables/constants/RowConstants';
import {TABLE_ROW, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {computeCustomRowLabel} from 'notebook/tables/store/logic/TableIdentityLogic';
import {
  AxisCellAffixedLayout,
  AxisHeaderModel,
  createAxisHeaderIdentitySelector,
  createAxisIsSelectedSelector,
  createAxisIsTableSelectedSelector,
  createAxisSelectionRangeSelector,
  UNAFFIXED_LAYOUT,
} from 'notebook/tables/store/models/AxisHeaderModel';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createRowStatusSelector} from 'notebook/tables/store/selectors/rowModel/RowStatusSelectors';
import {createTableStoreSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';

/**
 * The model for a single row header cell, keyed by row index. Rendered by `TableRow`.
 *
 * The row axis carries none of the column header's constraint-driven metadata (no rename, no
 * column type icon, no custom column highlight), so beyond the shared {@link AxisHeaderModel} this
 * holds the row axis's own permissions and the row metadata its cell renders. Row-specific values
 * that `TableRow` still passes to `TableAxisCell` as props — the row's in-view state — belong here
 * as they are migrated.
 */
export interface RowHeaderModel extends AxisHeaderModel {
  dim: TableDimension.ROW;
  /**
   * Whether this cell can toggle the header row. Only the first row's header offers the toggle, and
   * only when dimension edits are allowed and the legacy header toggle is not disabled.
   */
  canToggleHeader: boolean;
  /** Whether rows can be inserted above or below this one. */
  canInsertDims: boolean;
  /** Whether rows can be deleted. */
  canDeleteDims: boolean;
  /** Whether the whole table can be deleted. */
  canDeleteTable: boolean;
  /**
   * The row's data, or null when the table has none for this row (for example a plain table, or a
   * row header rendered outside of a table). Its user annotation indicator is rendered in the cell.
   */
  rowData: RowData | null;
  /**
   * The row's status — errors, warnings, staleness, archival and submission state — which drives
   * the cell's status styling and tooltip. Null for a row header rendered outside of a table, which
   * has no row to take a status from.
   */
  rowProperties: RowProperties | null;
}

/**
 * Creates a selector for a row header's {@link AxisCellAffixedLayout}. The row header is the
 * table's leading column, so it is always affixed at the left edge with no columns before it, and
 * it takes over casting the shadow over the scrolled content when no data column is frozen ahead
 * of it. That is the same for every row, so this takes no row index.
 */
function createRowHeaderAffixedLayoutSelector(
  tableSelector: (store: TableEditableStore) => TableModel
): (store: TableEditableStore) => AxisCellAffixedLayout {
  return createTableStoreSelector(
    {
      affixedColumnCount: (store) => tableSelector(store).affixed.affixedColumnCount,
      showAffixedShadow: (store) => tableSelector(store).affixed.showAffixedShadow,
      firstColumnWidth: (store) => tableSelector(store).measurements.firstColumnWidth,
    },
    ({affixedColumnCount, showAffixedShadow, firstColumnWidth}): AxisCellAffixedLayout => ({
      isAffixed: true,
      affixedLeftSpacing: 0,
      showAffixedShadow: showAffixedShadow && affixedColumnCount === 0,
      isLastFrozenColumn: false,
      isFirstColumn: firstColumnWidth > 0,
    })
  );
}

/** Creates a selector for the row header at `index` against the table store. */
export function createRowHeaderModelSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  index: number
): (store: TableEditableStore) => RowHeaderModel {
  return createTableStoreSelector(
    {
      identity: createAxisHeaderIdentitySelector(tableSelector, TABLE_ROW, index),
      selectionRange: createAxisSelectionRangeSelector(tableSelector, TABLE_ROW, index),
      isSelected: createAxisIsSelectedSelector(tableSelector, TABLE_ROW, index),
      isTableSelected: createAxisIsTableSelectedSelector(tableSelector, TABLE_ROW),
      affixedLayout: createRowHeaderAffixedLayoutSelector(tableSelector),
      customLabel: (store) => computeCustomRowLabel(tableSelector(store).props.table.rowLabels, index),
      canModifyDims: (store) => tableSelector(store).dimensions.canModifyDims,
      // `props` holds TableEditable's raw props, so the `??` default here repeats the one
      // `TableEditable`'s `propsWithDefaults` applies before rendering the class component.
      disableLegacyHeader: (store) => tableSelector(store).props.disableLegacyHeader ?? false,
      canModifyRowDim: (store) => tableSelector(store).dimensions.canModifyRowDim,
      canAddRows: (store) => tableSelector(store).dimensions.canAddRows,
      // The single-input view renders no row headers, so the `!isSingleInput` gate that
      // `TableEditable._getTRProps` applied to row deletion cannot be reached from here.
      canRemoveRows: (store) => tableSelector(store).dimensions.canRemoveRows,
      canDeleteTable: (store) => tableSelector(store).canDeleteTable,
      rowData: (store) => tableSelector(store).props.table.rowData?.[index] ?? null,
      // The same status the row itself renders from, so that the row header and its row cannot
      // disagree about whether the row has errors, is archived, and so on.
      rowProperties: createRowStatusSelector(tableSelector, index),
    },
    ({
      identity,
      selectionRange,
      isSelected,
      isTableSelected,
      affixedLayout,
      customLabel,
      canModifyDims,
      disableLegacyHeader,
      canModifyRowDim,
      canAddRows,
      canRemoveRows,
      canDeleteTable,
      rowData,
      rowProperties,
    }): RowHeaderModel => ({
      ...identity,
      dim: TABLE_ROW,
      isHeaderCell: false,
      customLabel,
      selectionRange,
      isSelected,
      isTableSelected,
      affixedLayout,
      canToggleHeader: index === 0 && canModifyDims && !disableLegacyHeader,
      canInsertDims: canModifyRowDim && canAddRows,
      canDeleteDims: canRemoveRows,
      canDeleteTable,
      rowData,
      rowProperties,
    })
  );
}

/**
 * A {@link RowHeaderModel} for a row header rendered outside of any table store, whose label comes
 * directly from its props rather than from a table. Such a header labels no table row, so it
 * permits none of the dimension actions, is never selected, and has no row data or status.
 */
export function makeStandaloneRowHeaderModel({
  index,
  customLabel = null,
  numDims = 0,
}: {
  index: number;
  customLabel?: ReactNode;
  numDims?: number;
}): RowHeaderModel {
  return {
    dim: TABLE_ROW,
    index,
    isGhostCell: false,
    numDims,
    isHeaderCell: false,
    customLabel,
    selectionRange: null,
    isSelected: false,
    isTableSelected: false,
    affixedLayout: UNAFFIXED_LAYOUT,
    canToggleHeader: false,
    canInsertDims: false,
    canDeleteDims: false,
    canDeleteTable: false,
    rowData: null,
    rowProperties: null,
  };
}
